import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Phone } from 'lucide-react';
import { api } from '@/lib/api';
import { Lead } from '@/types/lead';
import { LeadDetailPanel } from '@/components/LeadDetailPanel';
import { cn } from '@/lib/utils';

const SUB_DISPOSITIONS = [
  'Callback Requested',
  'Not Interested',
  'Already Joined',
  'Price Too High',
  'Need More Info',
  'Wrong Number',
  'No Answer',
  'Busy',
  'Follow Up Later',
  'Interested - Hot',
  'Interested - Warm',
];

const OVERDUE_PERIODS = [
  { label: 'Today', value: 'today' },
  { label: '3 Days', value: '3d' },
  { label: '7 Days', value: '7d' },
  { label: 'All Overdue', value: 'all' },
];

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function stageLabel(stage: string): string {
  const map: Record<string, string> = {
    NEW: 'New',
    CONTACTED: 'Contacted',
    DISCUSSION: 'Discussion',
    PROPOSAL: 'Proposal',
    NEGOTIATION: 'Negotiation',
    WON: 'Won',
    LOST: 'Lost',
  };
  return map[stage] || stage;
}

export const MissedFollowupsView: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [openPanel, setOpenPanel] = useState(false);
  const [daysOverdue, setDaysOverdue] = useState<string>('all');
  const [selectedSubDispositions, setSelectedSubDispositions] = useState<string[]>([]);
  const [showSubDispositionDropdown, setShowSubDispositionDropdown] = useState(false);

  const filters = {
    limit: 50,
    ...(selectedSubDispositions.length > 0 ? { subDisposition: selectedSubDispositions.join(',') } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['missed-followups', filters, daysOverdue],
    queryFn: () => api.getMissedFollowups(filters),
    staleTime: 2 * 60 * 1000,
  });

  const leads: any[] = data?.data?.leads || [];
  const total: number = data?.data?.total || 0;

  // Client-side filter by daysOverdue period
  const filteredLeads = leads.filter((row) => {
    if (daysOverdue === 'all') return true;
    const days = row.daysOverdue || 0;
    if (daysOverdue === 'today') return days <= 1;
    if (daysOverdue === '3d') return days <= 3;
    if (daysOverdue === '7d') return days <= 7;
    return true;
  });

  const handleOpenPanel = (lead: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // Convert to Lead shape expected by LeadDetailPanel
    const leadObj: Lead = {
      id: lead.id,
      name: lead.name,
      company: lead.company || '',
      email: lead.email || '',
      phoneNumber: lead.phoneNumber || '',
      dealValue: lead.dealValue || 0,
      stage: lead.stage as Lead['stage'],
      priority: (lead.priority || 'WARM') as Lead['priority'],
      assignedTo: lead.assignedTo?.id || '',
      assignedToUser: lead.assignedTo,
      subDisposition: lead.subDisposition,
      followUpDate: lead.followUpDate,
      createdAt: lead.createdAt || new Date().toISOString(),
      updatedAt: lead.updatedAt || new Date().toISOString(),
      lastActivity: lead.lastActivity || lead.updatedAt || new Date().toISOString(),
    } as Lead;
    setSelectedLead(leadObj);
    setOpenPanel(true);
  };

  const toggleSubDisposition = (value: string) => {
    setSelectedSubDispositions(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            Missed Follow-ups
            {total > 0 && (
              <span className="bg-red-100 text-red-700 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {total} overdue
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Leads where a follow-up was promised but has passed.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Period pills */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {OVERDUE_PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDaysOverdue(p.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                daysOverdue === p.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Sub-disposition multi-select */}
        <div className="relative">
          <button
            onClick={() => setShowSubDispositionDropdown(v => !v)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Outcome filter
            {selectedSubDispositions.length > 0 && (
              <span className="bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-xs font-semibold">
                {selectedSubDispositions.length}
              </span>
            )}
          </button>
          {showSubDispositionDropdown && (
            <div className="absolute z-20 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48">
              {SUB_DISPOSITIONS.map((d) => (
                <label key={d} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedSubDispositions.includes(d)}
                    onChange={() => toggleSubDisposition(d)}
                    className="rounded border-gray-300"
                  />
                  {d}
                </label>
              ))}
              {selectedSubDispositions.length > 0 && (
                <button
                  onClick={() => setSelectedSubDispositions([])}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 border-t border-gray-100"
                >
                  Clear selection
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <div className="bg-gray-200 animate-pulse rounded h-4 w-32" />
                <div className="bg-gray-200 animate-pulse rounded h-4 w-24 ml-4" />
                <div className="bg-gray-200 animate-pulse rounded h-4 w-28 ml-4" />
                <div className="bg-gray-200 animate-pulse rounded h-4 w-20 ml-auto" />
              </div>
            ))}
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No missed follow-ups — great work!</h3>
            <p className="text-sm text-gray-500">All follow-ups are on track.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Lead Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Sub-Disposition</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Follow-Up Was Due</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Days Overdue</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Assigned To</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLeads.map((row: any) => (
                  <tr
                    key={row.id}
                    onClick={() => handleOpenPanel(row)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                    <td className="px-4 py-3 text-gray-600">{row.phoneNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{row.subDisposition || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(row.followUpDate)}</td>
                    <td className="px-4 py-3">
                      <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                        {row.daysOverdue || 0}d overdue
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.assignedTo?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{stageLabel(row.stage || '')}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => handleOpenPanel(row, e)}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:underline font-medium"
                      >
                        <Phone className="w-3 h-3" />
                        Log Call
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* LeadDetailPanel */}
      <LeadDetailPanel
        lead={selectedLead}
        isOpen={openPanel}
        onClose={() => setOpenPanel(false)}
        onLeadUpdate={() => {
          queryClient.invalidateQueries({ queryKey: ['missed-followups'] });
          setOpenPanel(false);
        }}
        initialTab="interaction"
      />
    </div>
  );
};
